import moment, { MomentInput, unitOfTime } from "moment";
import { DeliveryWindow } from "records/item/delivery-window";
import { Range } from "types/cart/delivery-management";

export const canFitInDeliveryWindow = (
    earliestAvailableDate: moment.Moment,
    latestAvailableDate: moment.Moment | undefined,
    minimumDeliveryWindow: number,
    shipStart: moment.Moment,
    shipEnd: moment.Moment,
    enforceAvailableUntil: boolean | undefined,
    shipDates?: { ship_start: moment.Moment; ship_end: moment.Moment }[],
) => {
    // eslint-disable-next-line prefer-const
    let actualShipStart = shipStart.clone();
    let actualShipEnd = shipEnd.clone();

    if (actualShipStart.isBefore(earliestAvailableDate)) {
        // TODO: Fix when WO is updated to support user specified delivery windows
        // if (earliestAvailableDate.diff(shipEnd, "days") <= minimumDeliveryWindow) {
        //     actualShipStart = earliestAvailableDate.clone();
        // } else {
        return false;
        // }
    }
    if (latestAvailableDate) {
        if (actualShipEnd.isAfter(latestAvailableDate)) {
            return false;
        }
        if (latestAvailableDate.diff(shipStart, "days") >= minimumDeliveryWindow) {
            actualShipEnd = latestAvailableDate.clone();
        } else {
            return false;
        }
    }

    // If the window is too small to ship
    if (latestAvailableDate && actualShipEnd.diff(actualShipStart, "days") < minimumDeliveryWindow) {
        return false;
    }

    // if enforce_available_until is Off and available from date is
    // set in future it should show the Shipment model
    // only if a suitable shipment is not available
    if (
        earliestAvailableDate.isAfter() &&
        enforceAvailableUntil === false &&
        !checkShipmentExists({
            shipDates,
            startDate: earliestAvailableDate,
            endDate: latestAvailableDate,
            minimumDeliveryWindow,
        })
    ) {
        return false;
    }

    return true;
};

const checkShipmentExists = ({
    shipDates,
    startDate,
    endDate,
    minimumDeliveryWindow,
}: {
    shipDates: { ship_start: moment.Moment; ship_end: moment.Moment }[];
    startDate: moment.Moment;
    endDate: moment.Moment | undefined;
    minimumDeliveryWindow: number;
}) => {
    if (!Array.isArray(shipDates)) {
        return true;
    }

    return shipDates.some((shData) => {
        if (endDate) {
            return (
                moment.utc(shData.ship_start).isSame(startDate, "day") &&
                moment.utc(shData.ship_end).isSame(endDate, "day")
            );
        }
        return (
            moment.utc(shData.ship_start).isSame(startDate, "day") &&
            moment.utc(shData.ship_end).diff(startDate, "days") >= minimumDeliveryWindow
        );
    });
};

export const isPredefinedWindowMatch = ({ deliveryWindow, catalogWindowStart, catalogWindowEnd, catalogPrebook }) => {
    const startMatch = deliveryWindow.start.isSame(catalogWindowStart, "day");
    const endMatch = deliveryWindow.end.isSame(catalogWindowEnd, "day");
    const prebookMatch = deliveryWindow.prebook === catalogPrebook;

    return startMatch && endMatch && prebookMatch;
};

export const isAdjustableWindowMatch = ({ deliveryWindow, catalogWindowStart, catalogWindowEnd, catalogPrebook }) => {
    let startMatch;
    let endMatch;

    if (!deliveryWindow.earliest_available_date) {
        startMatch = false;
    } else {
        startMatch =
            deliveryWindow.earliest_available_date.isSameOrBefore(catalogWindowStart, "day") ||
            (deliveryWindow.earliest_available_date.isSameOrAfter(catalogWindowStart, "day") &&
                deliveryWindow.earliest_available_date.isSameOrBefore(catalogWindowEnd, "day"));
    }

    if (!deliveryWindow.latest_available_date) {
        endMatch = true;
    } else {
        endMatch = deliveryWindow.latest_available_date.isSameOrAfter(catalogWindowEnd, "day");
    }

    let prebookMatch = true;
    if (catalogPrebook !== null && catalogPrebook !== undefined) {
        prebookMatch = deliveryWindow.prebook === catalogPrebook;
    }

    return startMatch && endMatch && prebookMatch;
};

export const getMatchingWindow = ({ deliveryIds, deliveryWindowEntities, catalogDates, minDeliveryWindow }) => {
    let catalogDeliveryWindow;
    let matchingWindowId;
    const { catalogWindowStart, catalogWindowEnd, catalogPrebook } = catalogDates;
    // try to match defined windows
    deliveryIds.some((deliveryId) => {
        const deliveryWindow = deliveryWindowEntities[deliveryId];
        if (
            deliveryWindow.adjustable_dates === false &&
            isPredefinedWindowMatch({ deliveryWindow, catalogWindowStart, catalogWindowEnd, catalogPrebook })
        ) {
            matchingWindowId = deliveryId;
            catalogDeliveryWindow = deliveryWindow;
            return true;
        } else {
            return false;
        }
    });

    // then try to match prebook + adjustable windows
    if (!catalogDeliveryWindow) {
        deliveryIds.some((deliveryId) => {
            let deliveryWindow = deliveryWindowEntities[deliveryId];
            if (
                deliveryWindow.adjustable_dates === true &&
                isAdjustableWindowMatch({ deliveryWindow, catalogWindowStart, catalogWindowEnd, catalogPrebook })
            ) {
                matchingWindowId = deliveryId;
                deliveryWindow = deliveryWindow.set("start", catalogWindowStart);
                deliveryWindow = deliveryWindow.set("end", catalogWindowEnd);
                catalogDeliveryWindow = deliveryWindow;
                return true;
            } else {
                return false;
            }
        });
    }

    // Didn't match any adjustable, remove the prebook match part
    if (!catalogDeliveryWindow) {
        deliveryIds.some((deliveryId) => {
            let deliveryWindow = deliveryWindowEntities[deliveryId];
            const isMatch = isAdjustableWindowMatch({
                deliveryWindow,
                catalogWindowStart,
                catalogWindowEnd,
                catalogPrebook: null,
            });
            if (deliveryWindow.adjustable_dates === true && isMatch) {
                matchingWindowId = deliveryId;

                // override the adjustable window with dates from catalog, which should be legit for this delivery window
                deliveryWindow = deliveryWindow.set("start", catalogWindowStart);
                deliveryWindow = deliveryWindow.set("end", catalogWindowEnd);
                catalogDeliveryWindow = deliveryWindow;
                return true;
            } else {
                return false;
            }
        });
    }

    // Weird cases here -- e.g. today is in the middle of the selected delivery window, or product expires in the middle of the window...
    if (!catalogDeliveryWindow) {
        deliveryIds.some((deliveryId) => {
            let deliveryWindow = deliveryWindowEntities[deliveryId];
            let startMatch = deliveryWindow.earliest_available_date.isSameOrBefore(catalogWindowStart, "day");
            let endMatch = deliveryWindow.latest_available_date
                ? deliveryWindow.latest_available_date.isSameOrAfter(catalogWindowEnd, "day")
                : true;
            if (deliveryWindow.adjustable_dates) {
                if (startMatch) {
                    // might have to bump the date forward since 'today' could be in the middle of the selected window
                    deliveryWindow = deliveryWindow.set("start", catalogWindowStart);
                } else {
                    const startDayIsBeforeWindowClose = deliveryWindow.earliest_available_date.isSameOrBefore(
                        catalogWindowEnd,
                        "day",
                    );
                    if (startDayIsBeforeWindowClose) {
                        startMatch = true;
                        deliveryWindow = deliveryWindow.set("start", deliveryWindow.earliest_available_date);
                    }
                }

                if (endMatch) {
                    deliveryWindow = deliveryWindow.set("end", catalogWindowEnd);
                } else {
                    const endDayCutoff = catalogWindowEnd.clone().subtract(minDeliveryWindow, "days");
                    const deliveryWindowContainsLastPossibleDay = deliveryWindow.latest_available_date.isSameOrAfter(
                        endDayCutoff,
                        "day",
                    );
                    const catalogWindowExceedsProductAvailability = catalogWindowEnd.isAfter(
                        deliveryWindow.latest_available_date,
                        "day",
                    );

                    if (deliveryWindowContainsLastPossibleDay) {
                        deliveryWindow = deliveryWindow.set("end", endDayCutoff);
                        endMatch = true;
                    }

                    // E.g. catalog is March 1 - March 31 & product is avail Jan 1 - March 28, we should set end to march 28th
                    if (catalogWindowExceedsProductAvailability) {
                        deliveryWindow = deliveryWindow.set("end", deliveryWindow.latest_available_date);
                        endMatch = true;
                    }
                }

                if (startMatch && endMatch) {
                    matchingWindowId = deliveryId;
                    catalogDeliveryWindow = deliveryWindow;
                    return true;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        });
    }

    return { matchingWindowId, catalogDeliveryWindow };
};

export const isDeliveryWindowSuitable = (
    dw: DeliveryWindow,
    options: { start: MomentInput; end: MomentInput; prebook?: boolean },
) => {
    if (!dw) {
        return false;
    }
    if (options.prebook != null && !!dw.prebook !== !!options.prebook) {
        return false;
    }
    if (!dw.adjustable_dates) {
        return isSameRange(dw, options);
    }
    const left = !dw.earliest_available_date || dw.earliest_available_date.isSameOrBefore(options.start, "day");
    const right = !dw.latest_available_date || dw.latest_available_date.isSameOrAfter(options.end, "day");
    return left && right;
};

export const isSameRange = (a: Range, b: Range, granularity: unitOfTime.StartOf = "day") =>
    moment(a.start)?.isSame(b.start, granularity) && moment(a.end)?.isSame(b.end, granularity);
